import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOverlayContext } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
export const ALLOWED_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'csv'
];
export const DEFAULT_MAX_TOTAL_SIZE = 24 * 1024 * 1024; // 24MB in bytes
export const AttachFilesTestIds = getTestIds('AttachFilesTestIds');
export function useAttachFiles() {
    let params = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    const { maxTotalSize = DEFAULT_MAX_TOTAL_SIZE, initialAttachments = [], fetchAttachmentUrl, onFilesUploaded, onRemove } = params;
    const { api } = useStoreActions();
    const { showErrorToast } = useOverlayContext();
    const { browser: { uploadFileToCloudStorage } } = useIoContext();
    const { t: uploadErrorsTexts } = useLocaleKeys('invoice.attachments.uploadErrors');
    const [isUploading, setIsUploading] = useState(false);
    const [attachments, setAttachments] = useState(()=>initialAttachments.map((att)=>_object_spread_props(_object_spread({}, att), {
                loading: !att.url
            })));
    const urlCacheRef = useRef({});
    const fetchAttachmentUrlRef = useRef(fetchAttachmentUrl);
    fetchAttachmentUrlRef.current = fetchAttachmentUrl;
    const fetchUrlsForAttachments = (attachmentIds)=>{
        if (!fetchAttachmentUrlRef.current || attachmentIds.length === 0) return;
        attachmentIds.forEach(async (id)=>{
            if (urlCacheRef.current[id]) {
                setAttachments((prev)=>prev.map((att)=>att.id === id ? _object_spread_props(_object_spread({}, att), {
                            url: urlCacheRef.current[id],
                            loading: false
                        }) : att));
                return;
            }
            try {
                const url = await fetchAttachmentUrlRef.current(id);
                urlCacheRef.current[id] = url; // Cache the URL
                setAttachments((prev)=>prev.map((att)=>att.id === id ? _object_spread_props(_object_spread({}, att), {
                            url,
                            loading: false
                        }) : att));
            } catch (unused) {
                setAttachments((prev)=>prev.map((att)=>att.id === id ? _object_spread_props(_object_spread({}, att), {
                            loading: false
                        }) : att));
            }
        });
    };
    const setCurrentAttachments = useCallback((newAttachments)=>{
        const attachmentsWithCachedUrls = newAttachments.map((att)=>{
            const cachedUrl = urlCacheRef.current[att.id];
            return _object_spread_props(_object_spread({}, att), {
                url: cachedUrl || att.url,
                loading: !cachedUrl && !att.url
            });
        });
        setAttachments(attachmentsWithCachedUrls);
        const attachmentIdsNeedingUrl = attachmentsWithCachedUrls.filter((att)=>!att.url).map((att)=>att.id);
        fetchUrlsForAttachments(attachmentIdsNeedingUrl);
    }, []);
    useEffect(()=>{
        const attachmentIdsNeedingUrl = initialAttachments.filter((att)=>!att.url).map((att)=>att.id);
        fetchUrlsForAttachments(attachmentIdsNeedingUrl);
    }, [
        initialAttachments
    ]);
    const uploadFilesToGCS = async (files)=>{
        const filesMap = {};
        const filesData = [];
        files.forEach((file)=>{
            const id = generateUniqueId('attachment');
            filesData.push({
                id,
                displayName: file.name,
                size: file.size
            });
            filesMap[id] = file;
        });
        const { urls } = await api.business.getUploadUrls(filesData);
        await Promise.all(urls.map((param)=>{
            let { id, url, headers } = param;
            const file = filesMap[id];
            if (!file) throw new Error("File not found for id ".concat(id));
            return uploadFileToCloudStorage(url, file, headers);
        }));
        return filesData;
    };
    const handleUpload = async (files)=>{
        const loadingAttachments = files.map((file)=>({
                id: generateUniqueId('temp'),
                displayName: file.name,
                fileSize: file.size,
                loading: true
            }));
        setAttachments((prev)=>[
                ...prev,
                ...loadingAttachments
            ]);
        setIsUploading(true);
        try {
            const uploadedData = await uploadFilesToGCS(files);
            await (onFilesUploaded === null || onFilesUploaded === void 0 ? void 0 : onFilesUploaded(uploadedData));
            const uploadedAttachments = uploadedData.map((param)=>{
                let { id, displayName, size } = param;
                return {
                    id,
                    displayName,
                    fileSize: size,
                    loading: !!fetchAttachmentUrl
                };
            });
            setAttachments((prev)=>{
                const nonLoading = prev.filter((att)=>!loadingAttachments.some((la)=>la.id === att.id));
                return [
                    ...nonLoading,
                    ...uploadedAttachments
                ];
            });
            fetchUrlsForAttachments(uploadedData.map((param)=>{
                let { id } = param;
                return id;
            }));
        } catch (error) {
            setAttachments((prev)=>prev.filter((att)=>!loadingAttachments.some((la)=>la.id === att.id)));
            showErrorToast(uploadErrorsTexts.uploadFailed(), AttachFilesTestIds.UploadFailedErrorToast());
        } finally{
            setIsUploading(false);
        }
    };
    const processFiles = (files)=>{
        if (!files || files.length === 0) return;
        const allFiles = Array.from(files);
        const validFiles = allFiles.filter((file)=>{
            var _file_name_split_pop;
            const extension = (_file_name_split_pop = file.name.split('.').pop()) === null || _file_name_split_pop === void 0 ? void 0 : _file_name_split_pop.toLowerCase();
            return extension && ALLOWED_EXTENSIONS.includes(extension);
        });
        if (validFiles.length !== allFiles.length) {
            showErrorToast(uploadErrorsTexts.unsupportedFileType(), AttachFilesTestIds.UnsupportedFileTypeErrorToast());
            return;
        }
        const totalSize = attachments.reduce((sum, file)=>sum + file.fileSize, 0) + validFiles.reduce((sum, file)=>sum + file.size, 0);
        if (totalSize > maxTotalSize) {
            showErrorToast(uploadErrorsTexts.totalSizeExceeded({
                maxSizeMB: maxTotalSize / (1024 * 1024)
            }), AttachFilesTestIds.TotalSizeExceededErrorToast());
            return;
        }
        const existingFileNames = new Set(attachments.map((att)=>att.displayName));
        const newFileNames = validFiles.map((file)=>file.name);
        const hasDuplicateWithExisting = newFileNames.some((name)=>existingFileNames.has(name));
        const hasDuplicateWithinBatch = new Set(newFileNames).size !== newFileNames.length;
        if (hasDuplicateWithExisting || hasDuplicateWithinBatch) {
            showErrorToast(uploadErrorsTexts.duplicateFileName(), AttachFilesTestIds.DuplicateFileNameErrorToast());
            return;
        }
        handleUpload(validFiles);
    };
    const openFilePicker = ()=>{
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = ALLOWED_EXTENSIONS.map((ext)=>".".concat(ext)).join(',');
        input.onchange = ()=>{
            processFiles(input.files);
            input.remove();
        };
        input.click();
    };
    const handleRemove = useCallback(async (index)=>{
        const attachment = attachments[index];
        if (!attachment || attachment.loading) return;
        const previousAttachments = attachments;
        setAttachments((prev)=>prev.filter((_, i)=>i !== index));
        try {
            await (onRemove === null || onRemove === void 0 ? void 0 : onRemove(attachment));
        } catch (unused) {
            setAttachments(previousAttachments);
            showErrorToast(uploadErrorsTexts.removeFailed(), AttachFilesTestIds.RemoveFailedErrorToast());
        }
    }, [
        attachments,
        onRemove,
        showErrorToast
    ]);
    return {
        attachments,
        openFilePicker,
        processFiles,
        handleRemove,
        allowedExtensions: ALLOWED_EXTENSIONS,
        isUploading,
        setCurrentAttachments
    };
}
